// ═══════════════════════════════════════════════════════════════════════════
// DOMAIN: filtrage de la liste des actions — logique pure et partagée
// ═══════════════════════════════════════════════════════════════════════════
//
// Source de vérité UNIQUE du prédicat de filtrage de la liste #actions.
// Extraite de page-actions/render-page.ts (juin 2026) pour être réutilisée
// par la Matrice Eisenhower (#actions/matrix), qui doit reporter EXACTEMENT
// les filtres actifs sur la liste.
//
//   - ActionsFilterState : l'état des filtres (mêmes champs que les `f*` de
//     render-page, alignés sur les paramètres URL de la spec filtres_actions).
//   - parseActionsFilterState(qs) : query-string (hash) → état. Miroir de
//     `_parseUrlToState` de render-page pour les champs de FILTRE (les modes
//     « rattachement réunion » restent propres à la liste).
//   - filterActions(actions, state, ctx) : prédicat pur, sans I/O ni closure.
//
// `render-page.applyFilters` délègue ici ; garder les deux alignés revient
// donc à ne toucher qu'à ce fichier.

import { actionVisibleForStructure } from './structure-graph.js';

// ── Forme minimale d'une action pour le filtrage ──────────────────────────
export interface FilterableAction {
  id?: number;
  status_id?: number | null;
  priority?: number | null;
  approver_id?: number | null;
  owner_id?: number | null;
  performers?: Array<number | { id: number }> | string;
  consulted?: Array<number | { id: number }> | string;
  informed?: Array<number | { id: number }> | string;
  committee_ids?: number[];
  structure_ids?: number[];
  escalated_structure_ids?: number[];
  // Phases de plan d'action liées (juin 2026). Filtre « Plans d'action ».
  phase_ids?: number[];
  project_ids?: number[];
  updated_at?: string | null;
}

// ── État des filtres ──────────────────────────────────────────────────────
export interface ActionsFilterState {
  fAll: boolean;
  // Facettes RACI. Nommées d'après le rôle qu'elles filtrent, PAS d'après la
  // lettre : `fPerformer` = R (Réalise, `performers`), `fApprover` = A
  // (Approuve, `approver_id`). Les anciens noms (fOwner/fActor) laissaient
  // croire l'inverse — d'où l'inversion R↔A corrigée en août 2026.
  fPerformer: boolean;
  // Filtre « Owner » (juin 2026) : ne montrer que les actions dont l'user
  // courant est l'owner. Indépendant du scope RACI (combiné en ET).
  fOwnerScope: boolean;
  fApprover: boolean;
  fConsulted: boolean;
  fInformed: boolean;
  fTeamMode: boolean;
  fTeamId: string;
  fScope: boolean;
  fProgram: string;
  fProject: string;
  fCommittee: string;
  fStructure: string;
  // Filtre « Plans d'action » : 'all' | 'none' | id de phase (string).
  fPhase: string;
  fPriorityMin: number;
  fDateMode: string; // 'all' | 'today' | 'yest' | '7d' | 'custom'
  fDateStart: string;
  fDateEnd: string;
  fHideDone: boolean;
  fSelectedOnly: boolean;
}

// ── Contexte de résolution (maps + identité user) ─────────────────────────
export interface ActionsFilterCtx {
  userId: number | null;
  userTeamIds: number[];
  statusMap: Record<number, { is_final?: boolean }>;
  structParentMap: Record<number, number[]>;
  programs: Array<{ id: number; projects?: Array<{ id: number }> }>;
  selectedIds: Set<number>;
  /** Date du jour 'YYYY-MM-DD' (injectée pour rester testable et UTC-cohérent). */
  todayStr: string;
}

export function defaultActionsFilterState(): ActionsFilterState {
  return {
    fAll: false,
    fPerformer: true,
    fOwnerScope: false,
    fApprover: true,
    fConsulted: false,
    fInformed: false,
    fTeamMode: false,
    fTeamId: 'all',
    fScope: false,
    fProgram: 'all',
    fProject: 'all',
    fCommittee: 'all',
    fStructure: 'all',
    fPhase: 'all',
    fPriorityMin: 4,
    fDateMode: 'all',
    fDateStart: '',
    fDateEnd: '',
    fHideDone: true,
    fSelectedOnly: false,
  };
}

// ── Parsing query-string → état ───────────────────────────────────────────
// `qs` = la partie après '?' du hash (ex. 'all=1&prio=2&org=12'). Miroir
// fidèle des champs de filtre de render-page._parseUrlToState.
export function parseActionsFilterState(qs: string, today: string): ActionsFilterState {
  const s = defaultActionsFilterState();
  if (!qs) return s;
  const params = new URLSearchParams(qs);

  if (params.get('all') === '1') s.fAll = true;
  if (params.get('mode') === 'all') s.fAll = true; // rétro-compat
  if (params.get('owner') === '1') s.fOwnerScope = true;

  if (params.has('raci')) {
    const raci = params.get('raci') || '';
    s.fPerformer = raci.includes('R');
    s.fApprover = raci.includes('A');
    s.fConsulted = raci.includes('C');
    s.fInformed = raci.includes('I');
  }

  if (params.get('team') === '1') {
    s.fTeamMode = true;
    const tid = params.get('team_id');
    s.fTeamId = tid && /^\d+$/.test(tid) ? tid : 'all';
  }

  const prog = params.get('prog');
  if (prog) s.fProgram = prog;
  const proj = params.get('proj');
  if (proj) s.fProject = proj;
  s.fScope = s.fProgram !== 'all' || s.fProject !== 'all';

  const com = params.get('com');
  if (com) s.fCommittee = com;
  const org = params.get('org');
  if (org) s.fStructure = org;
  const phase = params.get('phase');
  if (phase) s.fPhase = phase;

  const prio = params.get('prio');
  if (prio != null && /^[0-4]$/.test(prio)) s.fPriorityMin = parseInt(prio);

  const act = params.get('act');
  if (act && ['today', 'yest', '7d', 'custom'].includes(act)) {
    s.fDateMode = act;
    if (act === 'custom') {
      s.fDateStart = params.get('from') || '';
      s.fDateEnd = params.get('to') || today;
    }
  }

  if (params.get('done') === '1') s.fHideDone = false;
  if (params.get('selected') === '1') s.fSelectedOnly = true;

  return s;
}

// ── Sérialisation état → query-string (inverse de parseActionsFilterState) ──
// Miroir EXACT de la partie « filtres » de render-page._serializeStateToUrl
// (les params propres à la liste — view/imp/urg/meeting — restent gérés là-bas).
// Les valeurs par défaut sont OMISES (URL courte) ; parse les recompose.
export function filterStateToParams(s: ActionsFilterState): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  if (s.fAll) out.push(['all', '1']);
  if (s.fOwnerScope) out.push(['owner', '1']);
  let raci = '';
  if (s.fPerformer) raci += 'R';
  if (s.fApprover) raci += 'A';
  if (s.fConsulted) raci += 'C';
  if (s.fInformed) raci += 'I';
  // Défaut "RA" : non écrit dans l'URL.
  if (raci !== 'RA') out.push(['raci', raci]);
  if (s.fTeamMode) {
    out.push(['team', '1']);
    if (s.fTeamId !== 'all') out.push(['team_id', String(s.fTeamId)]);
  }
  if (s.fScope) out.push(['scope', '1']);
  if (s.fProgram !== 'all') out.push(['prog', String(s.fProgram)]);
  if (s.fProject !== 'all') out.push(['proj', String(s.fProject)]);
  if (s.fCommittee !== 'all') out.push(['com', String(s.fCommittee)]);
  if (s.fStructure !== 'all') out.push(['org', String(s.fStructure)]);
  if (s.fPhase !== 'all') out.push(['phase', String(s.fPhase)]);
  if (s.fPriorityMin !== 4) out.push(['prio', String(s.fPriorityMin)]);
  if (s.fDateMode !== 'all') out.push(['act', s.fDateMode]);
  if (s.fDateMode === 'custom') {
    if (s.fDateStart) out.push(['from', s.fDateStart]);
    if (s.fDateEnd) out.push(['to', s.fDateEnd]);
  }
  if (!s.fHideDone) out.push(['done', '1']);
  if (s.fSelectedOnly) out.push(['selected', '1']);
  return out;
}

/** État des filtres → query-string (sans le '?'). */
export function serializeFilterStateToQuery(s: ActionsFilterState): string {
  return new URLSearchParams(filterStateToParams(s)).toString();
}

/**
 * Vrai si l'état est l'état par défaut « visible » (mêmes 15 champs que le
 * badge "filtres actifs" de render-page : ni fDateStart/fDateEnd ni
 * fSelectedOnly, qui ne déclenchent pas l'indicateur).
 */
export function isDefaultFilterState(s: ActionsFilterState): boolean {
  const d = defaultActionsFilterState();
  return (
    s.fAll === d.fAll &&
    s.fPerformer === d.fPerformer &&
    s.fOwnerScope === d.fOwnerScope &&
    s.fApprover === d.fApprover &&
    s.fConsulted === d.fConsulted &&
    s.fInformed === d.fInformed &&
    s.fTeamMode === d.fTeamMode &&
    s.fTeamId === d.fTeamId &&
    s.fScope === d.fScope &&
    s.fProgram === d.fProgram &&
    s.fProject === d.fProject &&
    s.fCommittee === d.fCommittee &&
    s.fStructure === d.fStructure &&
    s.fPhase === d.fPhase &&
    s.fPriorityMin === d.fPriorityMin &&
    s.fDateMode === d.fDateMode &&
    s.fHideDone === d.fHideDone
  );
}

// ── Prédicat de filtrage (pur) ────────────────────────────────────────────
// Copie fidèle de render-page.applyFilters, paramétrée par `state` + `ctx`.
export function filterActions<T extends FilterableAction>(
  actions: T[],
  state: ActionsFilterState,
  ctx: ActionsFilterCtx,
): T[] {
  const {
    fAll,
    fPerformer,
    fOwnerScope,
    fApprover,
    fConsulted,
    fInformed,
    fTeamMode,
    fTeamId,
    fScope,
    fProgram,
    fProject,
    fCommittee,
    fStructure,
    fPhase,
    fPriorityMin,
    fDateMode,
    fDateStart,
    fDateEnd,
    fHideDone,
    fSelectedOnly,
  } = state;
  const { userId, userTeamIds, statusMap, structParentMap, programs, selectedIds, todayStr } = ctx;

  const todayDate = todayStr;
  const dMinus1 = new Date();
  dMinus1.setDate(dMinus1.getDate() - 1);
  const dMinus7 = new Date();
  dMinus7.setDate(dMinus7.getDate() - 7);
  const yStr = dMinus1.toISOString().split('T')[0] ?? '';
  const wStr = dMinus7.toISOString().split('T')[0] ?? '';

  const matchTeamIds: number[] = (() => {
    if (fAll || !fTeamMode) return [];
    if (fTeamId === 'all') return userTeamIds;
    const tid = parseInt(fTeamId);
    return Number.isFinite(tid) ? [tid] : [];
  })();

  const ids = (list: unknown): number[] =>
    ((list as Array<number | { id: number }> | undefined) || []).map((x) =>
      typeof x === 'object' ? x.id : (x as number),
    );
  const matchIn = (list: unknown, allowTeam: boolean): boolean => {
    const xs = ids(list);
    if (userId != null && xs.includes(userId)) return true;
    if (allowTeam && matchTeamIds.length) {
      return matchTeamIds.some((tt) => xs.includes(tt));
    }
    return false;
  };

  return actions.filter((a: FilterableAction) => {
    // ── 1. Scope RACI ───────────────────────────────────────────────
    // R = Réalise → `performers` ; A = Approuve → `approver_id` (cf. la
    // convention RACI d'Ornexia). Avant août 2026 les deux étaient croisés :
    // cocher « (R)éalise » filtrait sur l'approbateur et inversement.
    if (!fAll) {
      let raciOk = false;
      if (fPerformer && matchIn(a.performers, true)) raciOk = true;
      if (!raciOk && fApprover && a.approver_id != null && a.approver_id === userId) raciOk = true;
      if (!raciOk && fConsulted && matchIn(a.consulted, true)) raciOk = true;
      if (!raciOk && fInformed && matchIn(a.informed, true)) raciOk = true;
      if (!raciOk) return false;
    }

    // ── 1bis. Filtre Owner : uniquement les actions dont je suis l'owner ──
    if (fOwnerScope) {
      if (userId == null || a.owner_id == null || a.owner_id !== userId) return false;
    }

    // ── 2. Périmètre (programme + projet) ───────────────────────────
    if (fScope) {
      if (fProgram === 'none') {
        const allProgramPids = new Set<number>();
        for (const p of programs) for (const proj of p.projects || []) allProgramPids.add(proj.id);
        if ((a.project_ids || []).some((id) => allProgramPids.has(id))) return false;
      } else if (fProgram !== 'all') {
        const progId = parseInt(fProgram);
        const prog = programs.find((p) => p.id === progId);
        if (!prog) return false;
        const progPids = (prog.projects || []).map((p) => p.id);
        if (!(a.project_ids || []).some((id) => progPids.includes(id))) return false;
      }
      if (fProject === 'none') {
        if ((a.project_ids || []).length > 0) return false;
      } else if (fProject !== 'all') {
        const pid = parseInt(fProject);
        if (!(a.project_ids || []).includes(pid)) return false;
      }
    }

    // ── 3. Comité (M2M) ─────────────────────────────────────────────
    if (fCommittee === 'none') {
      if ((a.committee_ids || []).length > 0) return false;
    } else if (fCommittee !== 'all') {
      const cid = parseInt(fCommittee);
      if (!(a.committee_ids || []).includes(cid)) return false;
    }

    // ── 3bis. Organisation / structure (M2M) + escalade ────────────────
    if (fStructure === 'none') {
      if ((a.structure_ids || []).length > 0) return false;
    } else if (fStructure !== 'all') {
      const sid = parseInt(fStructure);
      const visible = actionVisibleForStructure(
        a.structure_ids || [],
        a.escalated_structure_ids || [],
        sid,
        (cid) => structParentMap[cid] || [],
      );
      if (!visible) return false;
    }

    // ── 3ter. Plans d'action : phase/volet (M2M) ───────────────────────
    if (fPhase === 'none') {
      if ((a.phase_ids || []).length > 0) return false;
    } else if (fPhase !== 'all') {
      const phid = parseInt(fPhase);
      if (!(a.phase_ids || []).includes(phid)) return false;
    }

    // ── 4. Priorité (rang) ──────────────────────────────────────────
    if (fPriorityMin !== 4 && fPriorityMin != null) {
      const seuil = fPriorityMin;
      const prio = a.priority == null ? 2 : a.priority;
      if (prio > seuil) return false;
    }

    // ── 5. Activité (date de modification) ──────────────────────────
    const upd = a.updated_at ? (a.updated_at.split('T')[0] ?? null) : null;
    if (fDateMode === 'today') {
      if (!upd || upd !== todayDate) return false;
    } else if (fDateMode === 'yest') {
      if (!upd || upd < yStr) return false;
    } else if (fDateMode === '7d') {
      if (!upd || upd < wStr) return false;
    } else if (fDateMode === 'custom') {
      if (!upd) return false;
      if (fDateStart && upd < fDateStart) return false;
      if (fDateEnd && upd > fDateEnd) return false;
    }

    // ── 6. État (afficher les terminées) ────────────────────────────
    if (fHideDone) {
      const st = a.status_id != null ? statusMap[a.status_id] : undefined;
      if (st && st.is_final) return false;
    }

    // ── 7. "Afficher uniquement les sélectionnées" ──────────────────
    if (fSelectedOnly && (a.id == null || !selectedIds.has(a.id))) return false;

    return true;
  });
}
